import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'

import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import {
  EXPORT_SIMULATIONS_CSV_ASYNC,
  SET_SIMULATION_DATE_RANGE,
  SET_SIMULATION_DEPARTMENTS,
  SET_SIMULATION_SORT,
} from 'containers/SpearPhishingAgent/constants'
import {
  selectSimulationData,
  selectStatusCountsData,
} from 'containers/SpearPhishingAgent/selectors'
import { buildSimulationFilters } from 'containers/SpearPhishingAgent/utils'

import { formatDateRange } from 'components/lib/DesignSystem/V3/DateRangePicker'
import SmartFilter from 'components/pages/SpearPhishingAgent/components/Dashboard/components/SmartFilter'

import FiltersSection from './components/FiltersSection'
import SimulationsTable from './components/SimulationsTable'
import TopSection from './components/TopSection'

/* No panel behind the feed — cards sit directly on the page background, same as the
   dashboard's suggested simulations. */
const Container = styled.div`
  min-height: calc(100vh - 199px);

  /* New nav: the shell (top bar, global banner, page inset) replaces the legacy 64px header. */
  .platform-nav-content & {
    min-height: calc(
      100vh - var(--top-bar-height, 52px) - var(--banner-height, 0px) -
        (var(--page-inset, 20px) * 2) - 162px
    );
  }
`

/* Stopgap for the app-wide "* { font-family: Figtree }" in Typography.js, which hits
   every node directly and so defeats inheritance of the token font. A descendant
   universal selector outranks it and keeps the fix scoped to this page, header included.
   Remove once the global rule is scoped or dropped. */
const Page = styled.div`
  & * {
    font-family: var(--t-typography-font-family-font-family-default);
  }
`

/* MOTION: TBD in the DS 3.0 ruleset (proposal, not ratified), implemented as the working
   proposal like the Settings page: the chrome arrives once per mount, opacity 0 to 1 with an
   8px rise, entrance 400 on ease-emphasized, page sections 60ms apart. The three regions
   stay mounted through filtering, sorting and paging, so nothing replays. */
const regionEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
`

const Region = styled.div`
  animation: ${regionEnter} 400ms cubic-bezier(0.33, 0, 0.2, 1) both;
  animation-delay: ${({ $step }) => $step * 60}ms;
  /* backwards, not both: a lingering transform would trap dropdown layers under the
     next region (see the Settings page). The final state is the element's own. */
  animation-fill-mode: backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const SELECTION_MODES = {
  MANUAL: 'manual',
  MATCHING: 'matching',
}

const toggleId = (ids, id) =>
  ids.includes(id) ? ids.filter((listedId) => listedId !== id) : [...ids, id]

const Launched = () => {
  const simulationData = useSelector(selectSimulationData)
  const statusCountsData = useSelector(selectStatusCountsData)
  const companyFilter = useSelector(selectCompanyFilter)
  const dispatch = useDispatch()

  const { sort, dateRange, selectedDepartments } = simulationData
  const [selectionMode, setSelectionMode] = useState(SELECTION_MODES.MANUAL)
  const [selectedSimulationIds, setSelectedSimulationIds] = useState([])
  const [excludedSimulationIds, setExcludedSimulationIds] = useState([])
  const isMatchingSelection = selectionMode === SELECTION_MODES.MATCHING

  const catalogTotal = statusCountsData.data?.simulations?.total ?? 0
  const catalogDepartments = statusCountsData.data?.simulations?.departments
  const departmentOptions = useMemo(
    () =>
      (catalogDepartments || []).map((department) => ({ value: department, label: department })),
    [catalogDepartments]
  )

  const clearSelection = useCallback(() => {
    setSelectionMode(SELECTION_MODES.MANUAL)
    setSelectedSimulationIds([])
    setExcludedSimulationIds([])
  }, [])

  const listQueryKey = [
    simulationData.statusSelectedFilters.join('|'),
    selectedDepartments.join('|'),
    dateRange?.startDate ?? '',
    dateRange?.endDate ?? '',
    sort.key,
    sort.direction,
    companyFilter?.value ?? '',
  ].join('::')

  useEffect(() => {
    clearSelection()
  }, [clearSelection, listQueryKey])

  useEffect(
    () => () => {
      dispatch(EXPORT_SIMULATIONS_CSV_ASYNC.cancel())
    },
    [dispatch]
  )

  const resultsCount = simulationData.totalItems || 0
  const selectedCount = isMatchingSelection
    ? Math.max(0, resultsCount - excludedSimulationIds.length)
    : selectedSimulationIds.length
  const pageSelectedIds = isMatchingSelection
    ? simulationData.items.map(({ id }) => id).filter((id) => !excludedSimulationIds.includes(id))
    : selectedSimulationIds

  const toggleSimulation = useCallback(
    (id) => {
      const setIds = isMatchingSelection ? setExcludedSimulationIds : setSelectedSimulationIds
      setIds((previous) => toggleId(previous, id))
    },
    [isMatchingSelection]
  )

  const toggleAllVisible = useCallback(
    (pageIds = []) => {
      if (isMatchingSelection) {
        setExcludedSimulationIds((previous) => {
          const allChecked = pageIds.length > 0 && pageIds.every((id) => !previous.includes(id))
          if (allChecked) return [...new Set([...previous, ...pageIds])]
          return previous.filter((id) => !pageIds.includes(id))
        })
        return
      }
      setSelectedSimulationIds((previous) => {
        const allPicked = pageIds.length > 0 && pageIds.every((id) => previous.includes(id))
        if (allPicked) return previous.filter((id) => !pageIds.includes(id))
        return [...new Set([...previous, ...pageIds])]
      })
    },
    [isMatchingSelection]
  )

  const handleDepartmentsChange = useCallback(
    (value) => {
      dispatch({
        type: SET_SIMULATION_DEPARTMENTS,
        payload: { value: Array.isArray(value) ? value : [] },
      })
    },
    [dispatch]
  )

  const handleDateRangeChange = useCallback(
    (value) => {
      dispatch({ type: SET_SIMULATION_DATE_RANGE, payload: { value } })
    },
    [dispatch]
  )

  const handleSortChange = useCallback(
    (value) => {
      dispatch({ type: SET_SIMULATION_SORT, payload: { value } })
    },
    [dispatch]
  )

  const selectAllMatching = useCallback(() => {
    clearSelection()
    setSelectionMode(SELECTION_MODES.MATCHING)
  }, [clearSelection])

  const handleDownloadCsv = useCallback(() => {
    if (!isMatchingSelection) {
      dispatch(
        EXPORT_SIMULATIONS_CSV_ASYNC.initiate({ body: { includeIds: selectedSimulationIds } })
      )
      return
    }
    const body = buildSimulationFilters(simulationData)
    if (excludedSimulationIds.length) body.excludeIds = excludedSimulationIds
    dispatch(EXPORT_SIMULATIONS_CSV_ASYNC.initiate({ body }))
  }, [dispatch, excludedSimulationIds, isMatchingSelection, selectedSimulationIds, simulationData])

  const handleDownloadAllCsv = useCallback(() => {
    dispatch(EXPORT_SIMULATIONS_CSV_ASYNC.initiate({ body: {} }))
  }, [dispatch])

  const smartFilterLabel = dateRange?.startDate ? formatDateRange(dateRange) : null

  return (
    <Page>
      <Region $step={0}>
        <TopSection
          onDownloadCsv={handleDownloadAllCsv}
          downloadDisabled={
            simulationData.loader ||
            simulationData.listError ||
            simulationData.exportLoader ||
            catalogTotal === 0
          }
        />
      </Region>
      <Container>
        <Region $step={1}>
          <FiltersSection
            countsUnavailable={Boolean(simulationData.listError)}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            departmentOptions={departmentOptions}
            selectedDepartments={selectedDepartments}
            onDepartmentsChange={handleDepartmentsChange}
            selectedCount={selectedCount}
            totalCount={resultsCount}
            onSelectAllMatching={selectAllMatching}
            onDownloadCsv={handleDownloadCsv}
            onClearSelection={clearSelection}
          />
          <SmartFilter
            isVisible={Boolean(smartFilterLabel)}
            label={smartFilterLabel}
            resultsLabel={`${resultsCount} ${resultsCount === 1 ? 'result' : 'results'}`}
            onClear={() => handleDateRangeChange(null)}
          />
        </Region>
        <Region $step={2}>
          <SimulationsTable
            sort={sort}
            onSortChange={handleSortChange}
            selectedSimulationIds={pageSelectedIds}
            onToggleSimulation={toggleSimulation}
            onToggleAll={toggleAllVisible}
          />
        </Region>
      </Container>
    </Page>
  )
}

export default Launched
